import { supabase } from '@/integrations/supabase/client';
import { getReferralId } from './rewardfulUtils';
import { toast } from 'sonner';

/**
 * Creates a Stripe checkout session
 * @param email The email to associate with the checkout session
 * @param name The name to associate with the checkout session
 * @param discountType The discount type to apply (30, 50, or 997)
 * @returns The URL to redirect to for the checkout session
 */
export const createStripeCheckout = async (email: string, name: string = '', discountType: string | null = null): Promise<string> => {
  console.log("Creating Stripe checkout for email:", email || "anonymous user", "name:", name || "anonymous", "discount:", discountType);
  
  try {
    // Reduce timeout to 5 seconds to prevent UI blocking
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Tiempo de espera excedido al crear la sesión de pago")), 5000);
    });
    
    // Mostrar mensaje apropiado basado en el descuento
    if (discountType) {
      let discountMessage = "";
      if (discountType === "30") {
        discountMessage = "¡Descuento del 30% detectado! Precio final: 24.50€/mes";
      } else if (discountType === "50") {
        discountMessage = "¡Descuento del 50% detectado! Precio final: 17.50€/mes";
      } else if (discountType === "997") {
        discountMessage = "¡Precio especial detectado! Precio final: 9.97€/mes";
      }
      
      if (discountMessage) {
        toast.success(discountMessage, {
          description: "El descuento se aplicará automáticamente",
          duration: 5000
        });
      }
    }
    
    console.log("Sending request to create checkout session");
    
    const responsePromise = supabase.functions.invoke('verify-stripe-payment', {
      body: { 
        email,
        name,
        action: "create_checkout",
        trial_days: 3,
        discount_type: discountType
      }
    });
    
    const response = await Promise.race([responsePromise, timeoutPromise]);
    
    console.log("Stripe checkout response:", response);
    
    if (response.error) {
      console.error("Supabase function error:", response.error);
      throw new Error(`Error al llamar a la función de Supabase: ${response.error.message || JSON.stringify(response.error)}`);
    }
    
    const { data } = response;
    
    if (!data) {
      console.error("No data returned from verify-stripe-payment");
      throw new Error("No se recibió respuesta del servidor de pagos");
    }
    
    if (data.success === false) {
      console.error("Error from Stripe function:", data.error);
      throw new Error(`Error del servidor de pagos: ${data.error}`);
    }
    
    if (!data.url) {
      console.error("No URL in response data:", data);
      throw new Error("No se pudo crear la sesión de pago: falta URL de redirección");
    }
    
    // Mostrar confirmación del descuento aplicado
    if (data.discountApplied) {
      console.log("Discount applied:", data.discountApplied);
      toast.success(`${data.discountApplied} correctamente aplicado`, {
        description: `Precio final: ${(data.finalPrice / 100).toFixed(2)}€/mes`,
        duration: 6000
      });
    }
    
    return data.url;
  } catch (error) {
    console.error("Error creating checkout session:", error);
    throw error;
  }
};

/**
 * Creates a Stripe customer portal session
 * @param email The email associated with the customer
 * @returns The URL to redirect to for the customer portal session
 */
export const createStripePortalSession = async (email: string): Promise<string> => {
  console.log("Creating Stripe portal session for email:", email);
  
  try {
    // Reduce timeout to 5 seconds
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Tiempo de espera excedido al crear la sesión del portal")), 5000);
    });
    
    console.log("Sending request to create portal session", {
      email,
      action: "create_portal",
      return_url: `${window.location.origin}/suscripcion`,
      enable_cancellation: true
    });
    
    const responsePromise = supabase.functions.invoke('verify-stripe-payment', {
      body: { 
        email,
        action: "create_portal",
        return_url: `${window.location.origin}/suscripcion`,
        enable_cancellation: true
      }
    });
    
    const response = await Promise.race([responsePromise, timeoutPromise]);
    
    console.log("Stripe portal session response:", response);
    
    if (response.error) {
      console.error("Supabase function error:", response.error);
      throw new Error(`Error al llamar a la función de Supabase: ${response.error.message || JSON.stringify(response.error)}`);
    }
    
    const { data } = response;
    
    if (!data) {
      console.error("No data returned from verify-stripe-payment");
      throw new Error("No se recibió respuesta del servidor de pagos");
    }
    
    if (data.success === false) {
      console.error("Error from Stripe function:", data.error);
      throw new Error(`Error del servidor de pagos: ${data.error}`);
    }
    
    if (!data.url) {
      console.error("No URL in response data:", data);
      throw new Error("No se pudo crear la sesión del portal: falta URL de redirección");
    }
    
    console.log("About to return portal URL:", data.url);
    
    return data.url;
  } catch (error) {
    console.error("Error creating Stripe portal session:", error);
    throw error;
  }
};

/**
 * Verifies a Stripe payment and subscription status
 * @param sessionId The session ID to verify
 * @param email The email to verify
 * @returns The verified payment data including trial status
 */
export const verifyStripePayment = async (sessionId?: string, email?: string) => {
  console.log("Verifying Stripe payment:", { sessionId, email });
  
  if (!sessionId && !email) {
    console.error("No sessionId or email provided to verifyStripePayment");
    throw new Error("Se requiere un ID de sesión o email para verificar el pago");
  }
  
  try {
    // Reduce timeout to 5 seconds
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Tiempo de espera excedido al verificar el pago")), 5000);
    });
    
    console.log("Sending request to verify payment");
    
    const referralId = getReferralId();
    const extraData: Record<string, any> = {};
    
    if (referralId) {
      extraData.client_reference_id = referralId;
      extraData.referral_id = referralId;
    }
    
    const responsePromise = supabase.functions.invoke('verify-stripe-payment', {
      body: { 
        sessionId, 
        email,
        action: "verify_subscription",
        ...extraData
      }
    });
    
    const response = await Promise.race([responsePromise, timeoutPromise]);
    
    console.log("Verification response:", response);
    
    if (response.error) {
      console.error("Supabase function error during verification:", response.error);
      throw new Error(`Error al verificar el pago: ${response.error.message || JSON.stringify(response.error)}`);
    }
    
    if (!response.data) {
      console.error("No data returned from verify-stripe-payment");
      throw new Error("No se recibió respuesta del servidor de pagos");
    }
    
    return response.data;
  } catch (error) {
    console.error("Error verifying Stripe payment:", error);
    throw error;
  }
};

/**
 * Makes an authenticated call to admin-operations edge function
 * @param action The admin action to perform
 * @param params Additional parameters for the action
 * @returns The response data
 */
export const callAdminOperation = async (action: string, params: Record<string, any> = {}) => {
  console.log(`Calling admin operation: ${action}`, params);
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.error("No active session found for admin operation");
      throw new Error("No se encontró una sesión activa. Por favor, inicie sesión nuevamente.");
    }
    
    // Reduce timeout to 5 seconds
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`Tiempo de espera excedido en operación de administrador: ${action}`)), 5000);
    });
    
    const responsePromise = supabase.functions.invoke('admin-operations', {
      body: { 
        action,
        ...params
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    const response = await Promise.race([responsePromise, timeoutPromise]);
    
    console.log(`Admin operation ${action} response:`, response);
    
    if (response.error) {
      console.error(`Error in admin operation ${action}:`, response.error);
      throw new Error(`Error en operación de administrador: ${response.error.message || JSON.stringify(response.error)}`);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error calling admin operation ${action}:`, error);
    throw error;
  }
};
